import { saveAs } from "file-saver";
import { uniqueId } from "lodash";
import { create } from "zustand";
import useFilterStore from "./useFilterStore";

export type ExportKind = "website-export" | "structured-export";

export type ExcelExport = {
    id: string;
    kind: ExportKind;
    activeVersionId: string;
    activeDisciplineIds: string[];
    activeIfcVersionId: string | null;
    activeSoftwareId: string | null;
    activeUseCaseIds: string[];
    activeClassificationId: string | null;

    name?: string;
    abortController?: AbortController;
    blob?: Blob;
    done: boolean;
};

type ExportStore = {
    exportData: ExcelExport[];
    createExport: (kind?: ExportKind) => void;
    beginExport: (id: string, name: string, signal: AbortController) => void;
    finishExport: (id: string, blob?: Blob) => void;
    clearExport: (id: string) => void;
};

const useExportStore = create<ExportStore>()((set) => ({
    exportData: [],
    createExport: (kind: ExportKind = "website-export") => {
        const newExport: ExcelExport = {
            id: uniqueId(),
            kind,
            activeVersionId: useFilterStore.getState().activeVersion!,
            activeDisciplineIds: useFilterStore.getState().activeDisciplines,
            activeIfcVersionId: useFilterStore.getState().activeIfcVersion,
            activeSoftwareId: useFilterStore.getState().activeSoftware,
            activeUseCaseIds: useFilterStore.getState().activeUseCases,
            activeClassificationId: useFilterStore.getState().activeClassification,
            done: false,
        };

        set((state) => ({ exportData: [...state.exportData, newExport] }));
    },
    beginExport: (id: string, name: string, signal: AbortController) => {
        set((state) => {
            const exportItem = state.exportData.find((item) => item.id === id);
            if (exportItem) {
                exportItem.name = name;
                exportItem.abortController = signal;
            }

            return { exportData: [...state.exportData] };
        });
    },
    finishExport: (id: string, blob?: Blob) => {
        set((state) => {
            const exportItem = state.exportData.find((item) => item.id === id);
            if (exportItem) {
                exportItem.abortController?.abort();
                exportItem.done = true;
                
                if (blob) {
                    exportItem.blob = blob;
                    saveAs(blob, exportItem.name!);
                }
            }

            return { exportData: [...state.exportData] };
        });
    },
    clearExport: (id: string) => {
        set((state) => {
            // Cleanup the abort signal and then remove the value from the exportData array
            const exportItem = state.exportData.find((item) => item.id === id);
            if (exportItem) {
                exportItem.abortController?.abort();
                exportItem.blob = undefined;
            }

            return { exportData: state.exportData.filter((x) => x.id !== id) };
        });
    },
}));

export default useExportStore;
